/**
 * Jobs Calendar View
 *
 * Calendar view with drag-drop rescheduling for jobs.
 * PURELY PRESENTATIONAL - uses hooks for operations.
 *
 * DDD Compliance:
 * - Uses JobCardViewModel from application layer
 * - Uses calendar adapters to isolate library coupling
 * - Delegates reschedule to use case hook
 * - Shows client-side conflict warnings (server enforces)
 */

import { useState, useCallback } from 'react';
import { Calendar, dateFnsLocalizer, type Event as BigCalendarEvent } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import type { JobCardViewModel } from '@/application/service-provider/view-models/JobListViewModel';
import {
  fromJobsToCalendarEvents,
  getJobFromEvent,
  getEventStyle,
  type CalendarEvent,
} from '@/lib/calendarAdapters';
import { checkAllResourceConflicts } from '@/lib/resourceConflicts';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

// Configure date-fns localizer for react-big-calendar
const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Create drag-and-drop enabled calendar
const DragAndDropCalendar = withDragAndDrop(Calendar);

interface JobsCalendarViewProps {
  jobs: JobCardViewModel[];
  onReschedule: (jobId: string, newStartDate: Date) => Promise<void>;
  onViewDetails: (jobId: string) => void;
}

export function JobsCalendarView({
  jobs,
  onReschedule,
  onViewDetails,
}: JobsCalendarViewProps) {
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [conflictMessages, setConflictMessages] = useState<string[]>([]);
  const [pendingReschedule, setPendingReschedule] = useState<{
    jobId: string;
    newDate: Date;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Transform jobs to calendar events
  const events = fromJobsToCalendarEvents(jobs);

  // Handle event drop (drag-drop reschedule)
  const handleEventDrop = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (args: any) => {
      try {
        setErrorMessage(null); // Clear any previous errors
        const calendarEvent = args.event as CalendarEvent;
        const job = getJobFromEvent(calendarEvent);
        const newDate = args.start instanceof Date ? args.start : new Date(args.start);

        // Check for resource conflicts
        const conflicts = checkAllResourceConflicts(jobs, job, newDate);

        if (conflicts.length > 0) {
          // Show warning modal with conflict details
          const messages = conflicts
            .map(c => c.message)
            .filter((msg): msg is string => !!msg);

          setConflictMessages(messages);
          setShowConflictWarning(true);
          setPendingReschedule({ jobId: job.id, newDate });
          return; // Wait for user confirmation
        }

        // No conflicts - proceed with reschedule
        await onReschedule(job.id, newDate);
      } catch (error) {
        // Display user-friendly error message
        const message = error instanceof Error ? error.message : 'Failed to reschedule job';
        setErrorMessage(message);
        console.error('Reschedule error:', error);
      }
    },
    [jobs, onReschedule]
  );

  // Handle conflict warning confirmation
  const handleConfirmReschedule = useCallback(async () => {
    if (!pendingReschedule) return;

    try {
      setErrorMessage(null); // Clear any previous errors
      setShowConflictWarning(false);
      await onReschedule(pendingReschedule.jobId, pendingReschedule.newDate);
      setPendingReschedule(null);
      setConflictMessages([]);
    } catch (error) {
      // Display user-friendly error message
      const message = error instanceof Error ? error.message : 'Failed to reschedule job';
      setErrorMessage(message);
      console.error('Reschedule error:', error);
      setPendingReschedule(null);
      setConflictMessages([]);
    }
  }, [pendingReschedule, onReschedule]);

  // Handle conflict warning cancellation
  const handleCancelReschedule = useCallback(() => {
    setShowConflictWarning(false);
    setPendingReschedule(null);
    setConflictMessages([]);
  }, []);

  // Handle event selection (click)
  const handleSelectEvent = useCallback(
    (event: BigCalendarEvent) => {
      const calendarEvent = event as CalendarEvent;
      const job = getJobFromEvent(calendarEvent);
      onViewDetails(job.id);
    },
    [onViewDetails]
  );

  return (
    <>
      {/* Error Message Banner */}
      {errorMessage && (
        <div className="card mb-4">
          <div className="card-content">
            <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex-shrink-0">
                <svg
                  className="w-5 h-5 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-red-900 mb-1">
                  Failed to Reschedule Job
                </h4>
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
              <button
                onClick={() => setErrorMessage(null)}
                className="flex-shrink-0 text-red-600 hover:text-red-800"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-content" style={{ height: '700px' }}>
          {/* eslint-disable @typescript-eslint/no-explicit-any */}
          <DragAndDropCalendar
            localizer={localizer}
            events={events}
            startAccessor={(event: any) => event.start}
            endAccessor={(event: any) => event.end}
            style={{ height: '100%' }}
            onSelectEvent={handleSelectEvent}
            onEventDrop={handleEventDrop as any}
            draggableAccessor={() => true}
            resizable={false}
            eventPropGetter={(event: any) => getEventStyle(event as CalendarEvent)}
            views={['month', 'week', 'day']}
            defaultView="week"
            popup
            tooltipAccessor={(event: any) => {
              const calendarEvent = event as CalendarEvent;
              const job = getJobFromEvent(calendarEvent);
              return `${job.displayName} - ${job.statusBadge.label}`;
            }}
          />
          {/* eslint-enable @typescript-eslint/no-explicit-any */}
        </div>
      </div>

      {/* Conflict Warning Modal */}
      {showConflictWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card max-w-md w-full mx-4">
            <div className="card-content">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0">
                  <svg
                    className="w-6 h-6 text-yellow-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    Resource Conflicts Detected
                  </h3>
                  <p className="text-sm text-neutral-600 mb-3">
                    The following resource conflicts were detected:
                  </p>
                  <ul className="list-disc list-inside space-y-1 mb-4">
                    {conflictMessages.map((msg, idx) => (
                      <li key={idx} className="text-sm text-neutral-700">
                        {msg}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-neutral-600">
                    Do you want to proceed with rescheduling anyway? The server may
                    reject this change if conflicts violate business rules.
                  </p>
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  onClick={handleCancelReschedule}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmReschedule}
                  className="btn-primary bg-yellow-600 hover:bg-yellow-700"
                >
                  Proceed Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
